import { Badge, Body1, Button, Card, CardHeader, Image, InteractionTag, InteractionTagPrimary, InteractionTagSecondary, tokens, Tooltip } from '@fluentui/react-components';
import {
    ChevronLeftRegular,
    ChevronRightRegular,
    ArrowRepeatAll16Regular,
    Checkmark16Filled,
    Clipboard16Regular,
    LightbulbFilament16Regular,
    ThumbDislike16Filled,
    ThumbDislike16Regular,
    ThumbLike16Filled,
    ThumbLike16Regular,
} from '@fluentui/react-icons';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { AttachmentExtensionType, IAttachment, IFeedback, IRegeneratedAnswer, ISource } from '../../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import MarkdownPresenter from '../../Common/MarkdownPresenter';
import { DialogFeedbackAnswer } from '../../Dialogs/DialogFeedbackAnswer';
import './AnswerMessage.scss';
import { AnswerSources } from './AnswerSources';
import { useConversations, useFeature } from '../../../store/hooks';
import { getPluginIcon } from '../../../Utils/plugin';
import { getGptIconAsset } from '../../../Utils/image';
import { OpenAiLogo } from '../../Common/OpenAiLogo';
import { performEnterpriseGPTApiCall, useEnterpriseGPTApiCall } from '../../../Hooks/useMiddlewareApiCall';
import { FileTypeIcon } from '../../Common/FileTypeIcon';
import { useNotification } from "../../../Contexts/NotificationContext";

export interface AnswerMessageProps {
    id?: string;
    text: string;
    regeneratedAnswers?: IRegeneratedAnswer[];
    plugins?: string[];
    sources?: ISource[];
    chainOfThoughts?: string[];
    attachments?: IAttachment[];
    feedback?: IFeedback;
    isLast: boolean;
    onFeedback: (feedback: IFeedback) => void;
    onRegenerate: () => void;
    onSourceClick: (source: ISource) => void;
}

export const AnswerMessage: FC<AnswerMessageProps> = (props: AnswerMessageProps) => {
    const { id, text, regeneratedAnswers: regeneratedAnswers, chainOfThoughts, attachments, sources, feedback, onFeedback, onSourceClick } = props;
    const [answer, setAnswer] = useState<string>('');
    const imageName = useRef<string | undefined>(undefined);
    const [imageUrl, setImageUrl] = useState<string | undefined>(undefined);
    const minImageSize = 500;
    const maxImageSize = 800;
    const [imageSize, setImageSize] = useState<number>(minImageSize);
    const [copied, setCopied] = useState<boolean>(false);
    const [imageCopied, setImageCopied] = useState<boolean>(false);
    const [showDialog, setShowDialog] = useState<boolean>(false);
    const [showRegeneratedAnswers, setShowRegeneratedAnswers] = useState<boolean>(false);
    const [regeneratedAnswerIndex, setRegeneratedAnswerIndex] = useState<number>(0);
    const [showChainOfThoughts, setShowChainOfThoughts] = useState<boolean>(false);
    const [currentFeedback, setCurrentFeedback] = useState<IFeedback | undefined>(undefined);
    const { activeChat, activeGpt } = useConversations();
    const chatFeature = useFeature('chat');
    const { dispatchNotification } = useNotification();

    type FormulaReplacementKeys = '(' | ')' | '[' | ']';
    const formulaReplacements: Record<FormulaReplacementKeys, string> = {
        '(': '`$$',
        ')': '$$`',
        '[': '`$$',
        ']': '$$`',
    };

    // Update answer text
    useEffect(() => {
        const selectedAnswer = regeneratedAnswers?.[regeneratedAnswerIndex]?.answer ?? text ?? '';
        const updatedAnswer = selectedAnswer.replace(/\[source_id:(\d+(?:,\s*source_id:\d+)*)\]/g, (_, group: string) => {
            const incrementedIndices = group.split(/,\s*source_id:/).map((num) => {
                const index = (sources?.findIndex((s) => s.chunkId === num) ?? -1) + 1;
                return index;
            });
            return incrementedIndices.map((index) => `<sup class="sup">[${index}]</sup>`).join('');
        });
        setAnswer(updatedAnswer.replace(/\\([()\[\]])/g, (match, p1: FormulaReplacementKeys) => formulaReplacements[p1] || match));
    }, [activeChat, text, regeneratedAnswers, regeneratedAnswerIndex]);

    // Update answer image
    useEnterpriseGPTApiCall(
        async (client, abortSignal) => {
            const answerAttachment = regeneratedAnswers?.[regeneratedAnswerIndex]?.attachments?.[0] ?? attachments?.[0];
            if (answerAttachment?.extensionType === AttachmentExtensionType.Image) {
                if (answerAttachment?.name !== imageName.current) {
                    imageName.current = answerAttachment?.name;
                    const file = await client.downloadAttachment(activeChat?.id!, answerAttachment.name!, abortSignal);
                    const url = URL.createObjectURL(file.data);
                    setImageUrl(url);

                    return () => {
                        URL.revokeObjectURL(url);
                    };
                }
            } else {
                imageName.current = undefined;
                setImageUrl(undefined);
            }
        },
        [activeChat, attachments, regeneratedAnswers, regeneratedAnswerIndex],
    );

    const downloadDocumentAttachment = useCallback(
        async (filename: string) => {
            performEnterpriseGPTApiCall(async (client) => {
                if (activeChat && filename) {
                    const attachment = await client.downloadAttachment(activeChat?.id!, filename);
                    const url = URL.createObjectURL(attachment.data);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = decodeURI(filename);
                    a.click();

                    return () => {
                        URL.revokeObjectURL(url);
                    };
                }
            });
        },
        [activeChat],
    );

    useEffect(() => {
        if (regeneratedAnswers && regeneratedAnswers.length > 1) {
            setShowRegeneratedAnswers(true);
            setRegeneratedAnswerIndex(regeneratedAnswers.length - 1);
        }
    }, [regeneratedAnswers]);

    useEffect(() => {
        if (copied) {
            setTimeout(() => {
                setCopied(false);
            }, 2000);
        }
    }, [copied]);

    useEffect(() => {
        if (imageCopied) {
            setTimeout(() => {
                setImageCopied(false);
            }, 2000);
        }
    }, [imageCopied]);

    const onCopy = async () => {
        const answerContainer = document.getElementById(`answer_${id}`);

        if (answerContainer !== null) {
            // Get the original markdown text (before HTML processing)
            const originalMarkdown = regeneratedAnswers?.[regeneratedAnswerIndex]?.answer ?? text ?? '';
            try {
                const html = '<html><body><div><!--SyGPT-->' + answerContainer.outerHTML + '</div></body></html>';
  
                const clipboardItem = new ClipboardItem({
                    'text/plain': new Blob([answerContainer.innerText], { type: 'text/plain' }),
                    'text/html': new Blob([html], { type: 'text/html' }),
                    // Add markdown-specific MIME type for better compatibility
                    //'text/markdown': new Blob([originalMarkdown], { type: 'text/markdown' }),   // can cause problems ...
                });                
                await navigator.clipboard.write([clipboardItem]);
                setCopied(true);
            } catch (err) {
                // Fallback to plain text
                try {
                    await navigator.clipboard.writeText(answerContainer.innerText);
                    setCopied(true);
                } catch (fallbackErr) {
                    dispatchNotification('Copy failed. Please make sure the browser tab is focused.', 'error');                    
                }
            }
        }
    };

    const onCopyImage = useCallback(() => {
        performEnterpriseGPTApiCall(async (client) => {
            if (attachments) {
                let attachment = attachments[0];
                if (
                    regeneratedAnswers &&
                    regeneratedAnswers.length > 0 &&
                    regeneratedAnswers[regeneratedAnswerIndex].attachments &&
                    regeneratedAnswers[regeneratedAnswerIndex].attachments!.length > 0 &&
                    regeneratedAnswers[regeneratedAnswerIndex].attachments![0]
                ) {
                    attachment = regeneratedAnswers[regeneratedAnswerIndex].attachments![0];
                }

                if (activeChat && attachment?.name) {
                    const file = await client.downloadAttachment(activeChat?.id, attachment.name);

                    const clipboardItem = new ClipboardItem({
                        [file.data.type]: file.data,
                    });

                    navigator.clipboard.write([clipboardItem]);
                    setImageCopied(true);
                }
            }
        });
    }, [activeChat, attachments, regeneratedAnswers, regeneratedAnswerIndex]);

    return (
        <>
            <div className='answerMessage'>
                <div className='inner'>
                    <div className={activeGpt?.isDefault ? 'defaultGptLogo' : 'gptLogo'}>{activeGpt?.isDefault ? <OpenAiLogo size='small' /> : <Image src={getGptIconAsset(activeGpt?.icon)} />}</div>
                    <div className='content'>
                        <div id={`answer_${id}`} className='answerContent'>

                            <MarkdownPresenter key={'answer_' + id} text={answer} />

                            {imageUrl && (
                                <div>
                                    <Image
                                        className='answerAttachmentImage'
                                        data-zoom={imageSize === minImageSize ? 'in' : 'out'}
                                        key={`answer_img_${id}`}
                                        src={imageUrl}
                                        width={imageSize}
                                        onClick={() => setImageSize((size) => (size === minImageSize ? maxImageSize : minImageSize))}
                                        onError={() => setImageUrl(undefined)}
                                    />
                                    <Tooltip content={'Copy image'} relationship='label'>
                                        <Button
                                            className='imageCopyButton'
                                            size='medium'
                                            icon={imageCopied ? <Checkmark16Filled /> : <Clipboard16Regular />}
                                            appearance='transparent'
                                            shape='circular'
                                            onClick={onCopyImage}
                                        />
                                    </Tooltip>
                                </div>
                            )}
                            <div className='docAttachments'>
                                {attachments
                                    ?.filter((a) => a.extensionType !== AttachmentExtensionType.Image)
                                    .map((a) => (
                                        <InteractionTag key={a.name}>
                                            <InteractionTagPrimary icon={<FileTypeIcon filename={decodeURI(a.name!)} />} onClick={() => downloadDocumentAttachment(a.name!)}>
                                                {decodeURI(a.name!)}
                                            </InteractionTagPrimary>
                                        </InteractionTag>
                                    ))}
                            </div>
                        </div>
                        {sources && sources.length > 0 && <AnswerSources initialVisibleItemCount={sources.length} items={sources} onSourceClick={onSourceClick} />}
                        <div className='actionsContainer'>
                            <div className='actions'>
                                {showRegeneratedAnswers && regeneratedAnswers && (
                                    <>
                                        <Tooltip content={'Previous'} relationship='label'>
                                            <Button
                                                size='medium'
                                                icon={<ChevronLeftRegular />}
                                                appearance='transparent'
                                                shape='circular'
                                                onClick={() => setRegeneratedAnswerIndex((i) => i - 1)}
                                                disabled={regeneratedAnswerIndex === 0}
                                            />
                                        </Tooltip>
                                        {`${regeneratedAnswerIndex + 1}/${regeneratedAnswers?.length}`}
                                        <Tooltip content={'Next'} relationship='label'>
                                            <Button
                                                size='medium'
                                                icon={<ChevronRightRegular />}
                                                appearance='transparent'
                                                shape='circular'
                                                onClick={() => setRegeneratedAnswerIndex((i) => i + 1)}
                                                disabled={regeneratedAnswerIndex === regeneratedAnswers.length - 1}
                                            />
                                        </Tooltip>
                                    </>
                                )}
                                <Tooltip content={'Copy answer'} relationship='label'>
                                    <Button size='medium' icon={copied ? <Checkmark16Filled /> : <Clipboard16Regular />} appearance='transparent' shape='circular' onClick={onCopy} />
                                </Tooltip>

                                {props.isLast && (
                                    <Tooltip content={'Regenerate'} relationship='label'>
                                        <Button
                                            size='medium'
                                            icon={<ArrowRepeatAll16Regular />}
                                            appearance='transparent'
                                            shape='circular'
                                            onClick={props.onRegenerate}
                                            disabled={regeneratedAnswers && regeneratedAnswers.length === 5}
                                        />
                                    </Tooltip>
                                )}

                                <Tooltip content={'Give a positive feedback'} relationship='label'>
                                    <Button
                                        size='medium'
                                        icon={feedback?.positive ? <ThumbLike16Filled color={tokens.colorPaletteGreenBackground2} /> : <ThumbLike16Regular />}
                                        appearance='transparent'
                                        shape='circular'
                                        onClick={() => {
                                            setCurrentFeedback(feedback?.positive ? { ...feedback } : { positive: true });
                                            setShowDialog(true);
                                        }}
                                    />
                                </Tooltip>

                                <Tooltip content={'Give a negative feedback'} relationship='label'>
                                    <Button
                                        size='medium'
                                        icon={feedback?.positive === false ? <ThumbDislike16Filled color={tokens.colorPaletteRedBackground2} /> : <ThumbDislike16Regular />}
                                        appearance='transparent'
                                        shape='circular'
                                        onClick={() => {
                                            setCurrentFeedback(feedback?.positive === false ? { ...feedback } : { positive: false });
                                            setShowDialog(true);
                                        }}
                                    />
                                </Tooltip>

                                {chatFeature.conversation?.isChainOfThoughtsEnabled && (
                                    <Tooltip content={'Chain of thougths'} relationship='label'>
                                        <Button
                                            size='medium'
                                            icon={<LightbulbFilament16Regular />}
                                            appearance='transparent'
                                            shape='circular'
                                            disabled={!chainOfThoughts || chainOfThoughts.length === 0}
                                            onClick={() => setShowChainOfThoughts(!showChainOfThoughts)}
                                        />
                                    </Tooltip>
                                )}
                            </div>
                        </div>
                        {props.plugins?.map((p) => (
                            <Badge key={p} size='large' icon={getPluginIcon(p)}>
                                {activeGpt?.plugins?.find((fp) => fp.id == p)?.name}
                            </Badge>
                        ))}
                        {showChainOfThoughts && (
                            <Card>
                                <CardHeader description='List of reasoning steps that led to the answer:' />
                                {chainOfThoughts?.map((thought, index) => (
                                    <div className='thoughtItem' key={index}>
                                        <Badge className='thoughtItemIndex' appearance='outline' color='important'>
                                            {index + 1}
                                        </Badge>
                                        <Body1>{thought}</Body1>
                                    </div>
                                ))}
                            </Card>
                        )}
                    </div>
                </div>
            </div>
            <DialogFeedbackAnswer
                open={showDialog}
                feedback={currentFeedback}
                onFeedback={(feedback) => {
                    setShowDialog(false);
                    if (feedback) {
                        onFeedback(feedback);
                    }
                }}
            />
        </>
    );
};
